import { useEffect, useState } from "react";
import {
  Button,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import { Search, GetApp } from "@material-ui/icons";
import { api } from "../../services/apiService";
import {
  flaggedResult,
  healthResult,
  distractorResult,
  categoriesResult,
  trendResult,
  fmtNum,
  fmtPct,
  shortId,
  severityClass,
} from "./content-quality-logic";
import Menu from "../menu/menu";
import "./content-quality.scss";

// Master UI flag. The whole page renders a single "not enabled" notice unless this env
// flag is "true" AND the backend flag is on (a 404 also renders the dark notice). Not
// linked from any menu — reachable only by direct URL. Mirrors more-like-this-panel.
const FLAG_ON = process.env.REACT_APP_CONTENT_QUALITY_ENABLED === "true";

const DEFAULT_LIMIT = 50;

function SeverityChip({ severity }) {
  if (!severity) {
    return null;
  }
  return <Chip size="small" label={severity} className={`cq-chip ${severityClass(severity)}`} />;
}

function FlagChips({ flags }) {
  if (!Array.isArray(flags) || flags.length === 0) {
    return <span className="cq-muted">—</span>;
  }
  return (
    <div className="cq-flag-chips">
      {flags.filter(Boolean).map((f, i) => (
        <Chip
          key={`${f.code}-${i}`}
          size="small"
          label={f.code}
          title={f.message || ""}
          className={`cq-chip ${severityClass(f.severity)}`}
        />
      ))}
    </div>
  );
}

// The flagged worst-first table (loads on mount). Read-only; dark-safe.
function FlaggedTable() {
  const [result, setResult] = useState(null); // { state, data? }
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    api
      .getContentQualityFlagged(DEFAULT_LIMIT)
      .then((res) => active && setResult(flaggedResult(res)))
      .catch(() => active && setResult({ state: "error" }))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, []);

  if (loading) {
    return <CircularProgress size={22} className="cq-spinner" />;
  }
  if (!result || result.state === "dark") {
    return <Typography className="cq-empty">Content-quality analytics is not enabled yet.</Typography>;
  }
  if (result.state === "error") {
    return <Typography color="error" className="cq-error">Could not load flagged questions.</Typography>;
  }
  if (result.data.length === 0) {
    return <Typography className="cq-empty">No flagged questions — the sampled high-volume items look healthy.</Typography>;
  }

  return (
    <Table size="small" className="cq-table">
      <TableHead>
        <TableRow>
          <TableCell>Question</TableCell>
          <TableCell align="center">Severity</TableCell>
          <TableCell align="right">Attempts</TableCell>
          <TableCell align="right">p-value</TableCell>
          <TableCell align="right">Discrimination</TableCell>
          <TableCell>Flags</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {result.data.filter(Boolean).map((h) => {
          const d = h.difficulty || {};
          const disc = h.discrimination || {};
          return (
            <TableRow key={h.questionId} className={severityClass(h.overallSeverity)}>
              <TableCell title={h.questionId}><code>{shortId(h.questionId)}</code></TableCell>
              <TableCell align="center"><SeverityChip severity={h.overallSeverity} /></TableCell>
              <TableCell align="right">{d.attemptCount ?? "—"}</TableCell>
              <TableCell align="right">{fmtNum(d.pValue)}</TableCell>
              <TableCell align="right">{fmtNum(disc.pointBiserial)}</TableCell>
              <TableCell><FlagChips flags={h.flags} /></TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

// Per-question drill-down: type an id → composed health. Read-only; dark-safe.
function HealthLookup() {
  const [qid, setQid] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // { state, data? }

  const run = () => {
    const id = qid.trim();
    // Guard busy here too: the Enter key bypasses the button's disabled state,
    // and overlapping lookups race — the last to RESOLVE wins, so a stale
    // response could render one question's card under another's id.
    if (busy || !id) {
      return;
    }
    setBusy(true);
    setResult(null);
    api
      .getContentQualityHealth(id)
      .then((res) => setResult(healthResult(res)))
      .catch(() => setResult({ state: "error" }))
      .finally(() => setBusy(false));
  };

  return (
    <div className="cq-lookup">
      <div className="cq-lookup-bar">
        <TextField
          size="small"
          variant="outlined"
          label="Question id"
          value={qid}
          onChange={(e) => setQid(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && run()}
        />
        <Button
          variant="contained"
          color="primary"
          startIcon={<Search />}
          onClick={run}
          disabled={busy || !qid.trim()}
        >
          Inspect
        </Button>
      </div>

      {busy && <CircularProgress size={22} className="cq-spinner" />}
      {result && result.state === "dark" && (
        <Typography className="cq-empty">Content-quality analytics is not enabled yet.</Typography>
      )}
      {result && result.state === "error" && (
        <Typography color="error" className="cq-error">Could not load that question&apos;s health.</Typography>
      )}
      {result && result.state === "ok" && <HealthCard health={result.data} />}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <Paper className="cq-stat" elevation={0}>
      <div className="cq-stat-value">{value}</div>
      <div className="cq-stat-label">{label}</div>
    </Paper>
  );
}

// Per-question distractor breakdown (option shares, mean chooser score, key, flags).
// Read-only; dark-safe: on the dark/error state it silently omits the section rather
// than showing an error (the composed health above already covers the item).
function DistractorTable({ questionId }) {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    api
      .getContentQualityDistractors(questionId)
      .then((res) => active && setResult(distractorResult(res)))
      .catch(() => active && setResult({ state: "error" }))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [questionId]);

  if (loading) {
    return <CircularProgress size={18} className="cq-spinner" />;
  }
  if (!result || result.state === "dark" || result.state === "error") {
    return null;
  }
  const opts = Array.isArray(result.data.options) ? result.data.options : [];
  if (opts.length === 0) {
    return null;
  }
  return (
    <div className="cq-distractors">
      <Typography variant="caption" className="cq-subtitle">Answer options</Typography>
      <Table size="small" className="cq-table">
        <TableHead>
          <TableRow>
            <TableCell>Option</TableCell>
            <TableCell align="center">Key</TableCell>
            <TableCell align="right">Count</TableCell>
            <TableCell align="right">Share</TableCell>
            <TableCell align="right">Mean score</TableCell>
            <TableCell>Flag</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {opts.filter(Boolean).map((o, i) => (
            <TableRow key={`${o.value}-${i}`} className={o.flag ? "cq-sev-warning" : ""}>
              <TableCell><code>{o.value === "" ? "(blank)" : o.value}</code></TableCell>
              <TableCell align="center">{o.key ? "✓" : ""}</TableCell>
              <TableCell align="right">{o.count ?? "—"}</TableCell>
              <TableCell align="right">{fmtPct(o.share)}</TableCell>
              <TableCell align="right">{fmtNum(o.meanChooserScore)}</TableCell>
              <TableCell>
                {o.flag
                  ? <Chip size="small" label={o.flag} className="cq-chip cq-sev-warning" />
                  : <span className="cq-muted">—</span>}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

// SME CSV download. The global fetch is auth-patched, so we fetch the bytes and
// trigger a client-side download; a non-200 (dark/error) shows a quiet note.
function DownloadSmeSheet() {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(false);

  const download = () => {
    setBusy(true);
    setErr(false);
    api
      .getContentQualityDistractorCsv(50)
      .then((res) => {
        if (typeof Blob !== "undefined" && res instanceof Blob) {
          const href = URL.createObjectURL(res);
          const a = document.createElement("a");
          a.href = href;
          a.download = "content-quality-distractors.csv";
          document.body.appendChild(a);
          a.click();
          // Defer cleanup a tick — a few browsers cancel the download if the object URL
          // is revoked synchronously right after click().
          setTimeout(() => {
            a.remove();
            URL.revokeObjectURL(href);
          }, 0);
        } else {
          setErr(true); // { status } marker or unexpected → not available
        }
      })
      .catch(() => setErr(true))
      .finally(() => setBusy(false));
  };

  return (
    <div className="cq-download">
      <Button size="small" variant="outlined" startIcon={<GetApp />} onClick={download} disabled={busy}>
        Download SME sheet (CSV)
      </Button>
      {err && <Typography className="cq-muted" component="span"> — export not available.</Typography>}
    </div>
  );
}

// Drift severity → chip color: harder (content decay) is the more serious read.
function driftClass(flag) {
  switch (flag) {
    case "DRIFT_HARDER": return "cq-sev-critical";
    case "DRIFT_EASIER": return "cq-sev-warning";
    default: return "cq-sev-none";
  }
}

// Per-question difficulty trend (content-decay): a compact per-window p-value sparkline +
// drift badge + baseline→recent summary. Read-only; dark-safe (omits the section on 404/error).
function TrendPanel({ questionId }) {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    api
      .getContentQualityTrend(questionId)
      .then((res) => active && setResult(trendResult(res)))
      .catch(() => active && setResult({ state: "error" }))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [questionId]);

  if (loading) {
    return <CircularProgress size={18} className="cq-spinner" />;
  }
  if (!result || result.state === "dark" || result.state === "error") {
    return null;
  }
  const t = result.data;
  const windows = Array.isArray(t.windows) ? t.windows.filter(Boolean) : [];
  if (windows.length === 0) {
    return null;
  }
  return (
    <div className="cq-trend">
      <div className="cq-trend-head">
        <Typography variant="caption" className="cq-subtitle">Difficulty over time</Typography>
        {t.driftFlag && (
          <Chip size="small" label={t.driftFlag} className={`cq-chip ${driftClass(t.driftFlag)}`} />
        )}
      </div>
      <div className="cq-trend-bars">
        {windows.map((w, i) => (
          <div
            key={i}
            className="cq-trend-bar-wrap"
            title={`p=${fmtNum(w.pValue)}${w.sufficient ? "" : " (low n)"}`}
          >
            <div
              className="cq-trend-bar"
              style={{
                height: `${Math.round((w.pValue || 0) * 100)}%`,
                opacity: w.sufficient ? 1 : 0.4,
              }}
            />
          </div>
        ))}
      </div>
      <Typography variant="caption" className="cq-muted">
        baseline {fmtNum(t.baselinePValue)} → recent {fmtNum(t.recentPValue)}
        {t.pValueDelta !== null && t.pValueDelta !== undefined && ` (Δ ${fmtNum(t.pValueDelta)})`}
      </Typography>
    </div>
  );
}

function HealthCard({ health }) {
  const d = health.difficulty || {};
  const disc = health.discrimination || {};
  return (
    <Paper className={`cq-card ${severityClass(health.overallSeverity)}`} elevation={0}>
      <div className="cq-card-head">
        <code title={health.questionId}>{shortId(health.questionId, 14)}</code>
        <SeverityChip severity={health.overallSeverity} />
      </div>
      <div className="cq-stats">
        <Stat label="Attempts" value={d.attemptCount ?? "—"} />
        <Stat label={`p-value (${d.difficultyBand || "—"})`} value={fmtNum(d.pValue)} />
        <Stat label="Blank rate" value={fmtPct(d.blankRate)} />
        <Stat label={`Discrimination (${disc.discriminationBand || "—"})`} value={fmtNum(disc.pointBiserial)} />
        <Stat label="Disc. sample" value={disc.sampleSize ?? "—"} />
      </div>
      <div className="cq-card-flags">
        {Array.isArray(health.flags) && health.flags.filter(Boolean).length > 0 ? (
          health.flags.filter(Boolean).map((f, i) => (
            <div key={`${f.code}-${i}`} className={`cq-flag-row ${severityClass(f.severity)}`}>
              <Chip size="small" label={f.code} className={`cq-chip ${severityClass(f.severity)}`} />
              <span className="cq-flag-msg">{f.message}</span>
            </div>
          ))
        ) : (
          <Typography className="cq-muted">No anomaly flags — this item looks healthy.</Typography>
        )}
      </div>
      <DistractorTable questionId={health.questionId} />
      <TrendPanel questionId={health.questionId} />
    </Paper>
  );
}

const CATEGORY_DIMENSIONS = [
  { key: "organSystem", label: "Organ system" },
  { key: "discipline", label: "Discipline" },
  { key: "examType", label: "Exam type" },
];

// Topic/category health rollup with a dimension selector. Read-only; dark-safe (a 404
// renders "not enabled", any other failure a graceful error).
function CategoryRollup() {
  const [dimension, setDimension] = useState("organSystem");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setResult(null); // drop the prior dimension's rows so they can't flash under the new tab
    api
      .getContentQualityCategories(dimension, 50)
      .then((res) => active && setResult(categoriesResult(res)))
      .catch(() => active && setResult({ state: "error" }))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [dimension]);

  return (
    <div>
      <div className="cq-dim-tabs">
        {CATEGORY_DIMENSIONS.map((d) => (
          <Button
            key={d.key}
            size="small"
            variant={d.key === dimension ? "contained" : "outlined"}
            color={d.key === dimension ? "primary" : "default"}
            onClick={() => setDimension(d.key)}
          >
            {d.label}
          </Button>
        ))}
      </div>

      {loading && <CircularProgress size={22} className="cq-spinner" />}
      {!loading && (!result || result.state === "dark") && (
        <Typography className="cq-empty">Content-quality analytics is not enabled yet.</Typography>
      )}
      {!loading && result && result.state === "error" && (
        <Typography color="error" className="cq-error">Could not load the category rollup.</Typography>
      )}
      {!loading && result && result.state === "ok" && (
        result.data.length === 0 ? (
          <Typography className="cq-empty">No category data for this dimension.</Typography>
        ) : (
          <Table size="small" className="cq-table">
            <TableHead>
              <TableRow>
                <TableCell>Category</TableCell>
                <TableCell align="right">Questions</TableCell>
                <TableCell align="right">Responses</TableCell>
                <TableCell align="right">p-value</TableCell>
                <TableCell>Difficulty</TableCell>
                <TableCell align="right">Blank rate</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {result.data.filter(Boolean).map((c, i) => (
                <TableRow key={`${c.category}-${i}`}>
                  <TableCell>{c.category || "—"}</TableCell>
                  <TableCell align="right">{c.distinctQuestions ?? "—"}</TableCell>
                  <TableCell align="right">{c.responseCount ?? "—"}</TableCell>
                  <TableCell align="right">{fmtNum(c.pValue)}</TableCell>
                  <TableCell>{c.difficultyBand || "—"}</TableCell>
                  <TableCell align="right">{fmtPct(c.blankRate)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )
      )}
    </div>
  );
}

export default function ContentQualityPage() {
  if (!FLAG_ON) {
    return (
      <>
        <Menu />
        <div className="cq-wrap">
          <Typography className="cq-empty">Content-quality analytics is not enabled.</Typography>
        </div>
      </>
    );
  }

  return (
    <>
      <Menu />
      <div className="cq-wrap">
        <div className="cq-head">
          <Typography variant="h6" className="cq-title">Content-quality analytics</Typography>
          <Typography variant="caption" className="cq-subtitle">
            Read-only per-question health from existing responses — item difficulty,
            discrimination, and anomaly flags. Advisory only.
          </Typography>
        </div>

        <div className="cq-section">
          <Typography variant="subtitle1" className="cq-section-title">Questions needing attention</Typography>
          <DownloadSmeSheet />
          <FlaggedTable />
        </div>

        <div className="cq-section">
          <Typography variant="subtitle1" className="cq-section-title">Content by topic</Typography>
          <CategoryRollup />
        </div>

        <div className="cq-section">
          <Typography variant="subtitle1" className="cq-section-title">Inspect a question</Typography>
          <HealthLookup />
        </div>
      </div>
    </>
  );
}
